import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import AHDate from "_shared/dates/date";
import DateSchema from "_shared/dates/date-schema";
import Guid from "_shared/guids/guid";
import { ProposalBoxType, ProposalContext, ProposalEvent, ProposalStatus } from "work/proposals/__constants/constants";
import { z } from "zod";

export type ProposalFilterJSON = z.infer<typeof ProposalFilterSchema.ProposalFilterJSONZ>;
export type ProposalfilterParametersProps = z.infer<typeof ProposalFilterSchema.ProposalFilterZ>;

export default class ProposalFilterSchema {
  public static readonly ProposalFilterJSONZ = z.object({
    query: z.string().optional(),
    RFPId: z.uuid().optional(),
    name: z.string().optional(),
    description: z.string().optional(),
    clientUserId: z.uuid().optional(),
    clientEntityId: z.uuid().optional(),
    clientTeamMemberUserId: z.uuid().optional(),
    clientTeamMemberEntityId: z.uuid().optional(),
    hasStart: z.boolean().optional(),
    minStart: z.iso.datetime({ offset: true }).optional(),
    maxStart: z.iso.datetime({ offset: true }).optional(),
    hasEnd: z.boolean().optional(),
    minEnd: z.iso.datetime({ offset: true }).optional(),
    maxEnd: z.iso.datetime({ offset: true }).optional(),
    vendorTeamRestricted: z.boolean().optional(),
    vendorUserId: z.uuid().optional(),
    vendorEntityId: z.uuid().optional(),
    VendorTeamMemberUserId: z.uuid().optional(),
    vendorTeamMemberEntityId: z.uuid().optional(),
    additionalVendorUserId: z.uuid().optional(),
    additionalVendorEntityId: z.uuid().optional(),
    feeScheduleCategoryId: z.uuid().optional(),
    feeScheduleCategoryName: z.string().optional(),
    feeScheduleCategoryDescription: z.string().optional(),
    teamTemplateId: z.uuid().optional(),
    teamTemplateName: z.string().optional(),
    feeScheduleTemplateId: z.uuid().optional(),
    feeScheduleTemplateName: z.string().optional(),
    minDiscount: z.number().optional(),
    maxDiscount: z.number().optional(),
    minCreated: z.iso.datetime({ offset: true }).optional(),
    maxCreated: z.iso.datetime({ offset: true }).optional(),
    isNegotiable: z.boolean().optional(),
    hasResponseDueBy: z.boolean().optional(),
    minResponseDueBy: z.iso.datetime({ offset: true }).optional(),
    maxResponseDueBy: z.iso.datetime({ offset: true }).optional(),
    isSuperseded: z.boolean().optional(),
    supersededByProposalId: z.uuid().optional(),
    clientReviewerId: z.uuid().optional(),
    vendorReviewerId: z.uuid().optional(),
    status: z.enum(ProposalStatus).optional(),
    event: z.enum(ProposalEvent).optional(),
    eventActorUserId: z.uuid().optional(),
    minEventDate: z.iso.datetime({ offset: true }).optional(),
    maxEventDate: z.iso.datetime({ offset: true }).optional(),
    boxType: z.enum(ProposalBoxType).optional(),
    context: z.enum(ProposalContext).optional(),
    isDeleted: z.boolean().optional(),
    minDeleted: z.iso.datetime({ offset: true }).optional(),
    maxDeleted: z.iso.datetime({ offset: true }).optional(),
    userId: z.uuid().optional(),
  });
  public static readonly ProposalFilterZ = z.object({
    query: z.string().optional(),
    RFPId: z.instanceof(Guid).optional(),
    name: z.string().optional(),
    description: z.string().optional(),
    clientUserId: z.instanceof(Guid).optional(),
    clientEntityId: z.instanceof(Guid).optional(),
    clientTeamMemberUserId: z.instanceof(Guid).optional(),
    clientTeamMemberEntityId: z.instanceof(Guid).optional(),
    hasStart: z.boolean().optional(),
    minStart: DateSchema.type.optional(),
    maxStart: DateSchema.type.optional(),
    hasEnd: z.boolean().optional(),
    minEnd: DateSchema.type.optional(),
    maxEnd: DateSchema.type.optional(),
    vendorTeamRestricted: z.boolean().optional(),
    vendorUserId: z.instanceof(Guid).optional(),
    vendorEntityId: z.instanceof(Guid).optional(),
    vendorTeamMemberUserId: z.instanceof(Guid).optional(),
    vendorTeamMemberEntityId: z.instanceof(Guid).optional(),
    additionalVendorUserId: z.instanceof(Guid).optional(),
    additionalVendorEntityId: z.instanceof(Guid).optional(),
    feeScheduleCategoryId: z.instanceof(Guid).optional(),
    feeScheduleCategoryName: z.string().optional(),
    feeScheduleCategoryDescription: z.string().optional(),
    teamTemplateId: z.instanceof(Guid).optional(),
    teamTemplateName: z.string().optional(),
    feeScheduleTemplateId: z.instanceof(Guid).optional(),
    feeScheduleTemplateName: z.string().optional(),
    minDiscount: z.number().optional(),
    maxDiscount: z.number().optional(),
    minCreated: DateSchema.type.optional(),
    maxCreated: DateSchema.type.optional(),
    isNegotiable: z.boolean().optional(),
    hasResponseDueBy: z.boolean().optional(),
    minResponseDueBy: DateSchema.type.optional(),
    maxResponseDueBy: DateSchema.type.optional(),
    isSuperseded: z.boolean().optional(),
    supersededByProposalId: z.instanceof(Guid).optional(),
    clientReviewerId: z.instanceof(Guid).optional(),
    vendorReviewerId: z.instanceof(Guid).optional(),
    status: z.enum(ProposalStatus).optional(),
    event: z.enum(ProposalEvent).optional(),
    eventActorUserId: z.instanceof(Guid).optional(),
    minEventDate: DateSchema.type.optional(),
    maxEventDate: DateSchema.type.optional(),
    boxType: z.enum(ProposalBoxType).optional(),
    context: z.enum(ProposalContext).optional(),
    isDeleted: z.boolean().optional(),
    minDeleted: DateSchema.type.optional(),
    maxDeleted: DateSchema.type.optional(),
    userId: z.instanceof(Guid).optional(),
  });

  public static validateJSON(values: unknown): ProposalfilterParametersProps {
    return parseOrThrow(ProposalFilterSchema.ProposalFilterZ, values, {
      logError: true,
      schemaName: "ProposalFilterSchema",
      operation: "validateJSON",
      payloadSample: values,
    });
  }

  public static isValidJSON(values: unknown): values is ProposalfilterParametersProps {
    try {
      ProposalFilterSchema.validateJSON(values);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(data: ProposalFilterJSON): ProposalfilterParametersProps {
    const parsed = parseOrThrow(ProposalFilterSchema.ProposalFilterJSONZ, data, {
      logError: true,
      schemaName: "ProposalFilterSchema",
      operation: "deserialize",
      payloadSample: data,
    });
    return ProposalFilterSchema.codec.decode(parsed);
  }

  public static serialize(props: ProposalfilterParametersProps): ProposalFilterJSON {
    const parsed = ProposalFilterSchema.validateJSON(props);
    return ProposalFilterSchema.codec.encode(parsed);
  }

  private static readonly codec = z.codec(
    ProposalFilterSchema.ProposalFilterJSONZ,
    ProposalFilterSchema.ProposalFilterZ,
    {
      decode: (data) => ({
        query: data.query,
        RFPId: Guid.fromJSON(data.RFPId) ?? undefined,
        name: data.name,
        description: data.description,
        clientUserId: Guid.fromJSON(data.clientUserId) ?? undefined,
        clientEntityId: Guid.fromJSON(data.clientEntityId) ?? undefined,
        clientTeamMemberUserId: Guid.fromJSON(data.clientTeamMemberUserId) ?? undefined,
        clientTeamMemberEntityId: Guid.fromJSON(data.clientTeamMemberEntityId) ?? undefined,
        hasStart: data.hasStart,
        minStart: AHDate.fromJSON(data.minStart) ?? undefined,
        maxStart: AHDate.fromJSON(data.maxStart) ?? undefined,
        hasEnd: data.hasEnd,
        minEnd: AHDate.fromJSON(data.minEnd) ?? undefined,
        maxEnd: AHDate.fromJSON(data.maxEnd) ?? undefined,
        vendorTeamRestricted: data.vendorTeamRestricted,
        vendorUserId: Guid.fromJSON(data.vendorUserId) ?? undefined,
        vendorEntityId: Guid.fromJSON(data.vendorEntityId) ?? undefined,
        VendorTeamMemberUserId: Guid.fromJSON(data.VendorTeamMemberUserId) ?? undefined,
        vendorTeamMemberEntityId: Guid.fromJSON(data.vendorTeamMemberEntityId) ?? undefined,
        additionalVendorUserId: Guid.fromJSON(data.additionalVendorUserId) ?? undefined,
        additionalVendorEntityId: Guid.fromJSON(data.additionalVendorEntityId) ?? undefined,
        feeScheduleCategoryId: Guid.fromJSON(data.feeScheduleCategoryId) ?? undefined,
        feeScheduleCategoryName: data.feeScheduleCategoryName,
        feeScheduleCategoryDescription: data.feeScheduleCategoryDescription,
        teamTemplateId: Guid.fromJSON(data.teamTemplateId) ?? undefined,
        teamTemplateName: data.teamTemplateName,
        feeScheduleTemplateId: Guid.fromJSON(data.feeScheduleTemplateId) ?? undefined,
        feeScheduleTemplateName: data.feeScheduleTemplateName,
        minDiscount: data.minDiscount,
        maxDiscount: data.maxDiscount,
        minCreated: AHDate.fromJSON(data.minCreated) ?? undefined,
        maxCreated: AHDate.fromJSON(data.maxCreated) ?? undefined,
        isNegotiable: data.isNegotiable,
        hasResponseDueBy: data.hasResponseDueBy,
        minResponseDueBy: AHDate.fromJSON(data.minResponseDueBy) ?? undefined,
        maxResponseDueBy: AHDate.fromJSON(data.maxResponseDueBy) ?? undefined,
        isSuperseded: data.isSuperseded,
        supersededByProposalId: Guid.fromJSON(data.supersededByProposalId) ?? undefined,
        clientReviewerId: Guid.fromJSON(data.clientReviewerId) ?? undefined,
        vendorReviewerId: Guid.fromJSON(data.vendorReviewerId) ?? undefined,
        status: data.status ? z.enum(ProposalStatus).parse(data.status) : undefined,
        event: data.event ? z.enum(ProposalEvent).parse(data.event) : undefined,
        eventActorUserId: Guid.fromJSON(data.eventActorUserId) ?? undefined,
        minEventDate: AHDate.fromJSON(data.minEventDate) ?? undefined,
        maxEventDate: AHDate.fromJSON(data.maxEventDate) ?? undefined,
        boxType: data.boxType ? z.enum(ProposalBoxType).parse(data.boxType) : undefined,
        context: data.context ? z.enum(ProposalContext).parse(data.context) : undefined,
        isDeleted: data.isDeleted,
        minDeleted: AHDate.fromJSON(data.minDeleted) ?? undefined,
        maxDeleted: AHDate.fromJSON(data.maxDeleted) ?? undefined,
        userId: Guid.fromJSON(data.userId) ?? undefined,
      }),
      encode: (proposal) => ({
        query: proposal.query,
        RFPId: proposal.RFPId?.toJSON(),
        name: proposal.name,
        description: proposal.description,
        clientUserId: proposal.clientUserId?.toJSON(),
        clientEntityId: proposal.clientEntityId?.toJSON(),
        clientTeamMemberUserId: proposal.clientTeamMemberUserId?.toJSON(),
        clientTeamMemberEntityId: proposal.clientTeamMemberEntityId?.toJSON(),
        hasStart: proposal.hasStart,
        minStart: proposal.minStart?.toJSON(),
        maxStart: proposal.maxStart?.toJSON(),
        hasEnd: proposal.hasEnd,
        minEnd: proposal.minEnd?.toJSON(),
        maxEnd: proposal.maxEnd?.toJSON(),
        vendorTeamRestricted: proposal.vendorTeamRestricted,
        vendorUserId: proposal.vendorUserId?.toJSON(),
        vendorEntityId: proposal.vendorEntityId?.toJSON(),
        VendorTeamMemberUserId: proposal.vendorTeamMemberUserId?.toJSON(),
        vendorTeamMemberEntityId: proposal.vendorTeamMemberEntityId?.toJSON(),
        additionalVendorUserId: proposal.additionalVendorUserId?.toJSON(),
        additionalVendorEntityId: proposal.additionalVendorEntityId?.toJSON(),
        feeScheduleCategoryId: proposal.feeScheduleCategoryId?.toJSON(),
        feeScheduleCategoryName: proposal.feeScheduleCategoryName,
        feeScheduleCategoryDescription: proposal.feeScheduleCategoryDescription,
        teamTemplateId: proposal.teamTemplateId?.toJSON(),
        teamTemplateName: proposal.teamTemplateName,
        feeScheduleTemplateId: proposal.feeScheduleTemplateId?.toJSON(),
        feeScheduleTemplateName: proposal.feeScheduleTemplateName,
        minDiscount: proposal.minDiscount,
        maxDiscount: proposal.maxDiscount,
        minCreated: proposal.minCreated?.toJSON(),
        maxCreated: proposal.maxCreated?.toJSON(),
        isNegotiable: proposal.isNegotiable,
        hasResponseDueBy: proposal.hasResponseDueBy,
        minResponseDueBy: proposal.minResponseDueBy?.toJSON(),
        maxResponseDueBy: proposal.maxResponseDueBy?.toJSON(),
        isSuperseded: proposal.isSuperseded,
        supersededByProposalId: proposal.supersededByProposalId?.toJSON(),
        clientReviewerId: proposal.clientReviewerId?.toJSON(),
        vendorReviewerId: proposal.vendorReviewerId?.toJSON(),
        status: proposal.status,
        event: proposal.event,
        eventActorUserId: proposal.eventActorUserId?.toJSON(),
        minEventDate: proposal.minEventDate?.toJSON(),
        maxEventDate: proposal.maxEventDate?.toJSON(),
        boxType: proposal.boxType,
        context: proposal.context,
        isDeleted: proposal.isDeleted,
        minDeleted: proposal.minDeleted?.toJSON(),
        maxDeleted: proposal.maxDeleted?.toJSON(),
        userId: proposal.userId?.toJSON(),
      }),
    }
  );
}
